import type { FC, ReactNode } from "react";
import { Link } from "react-router";
import { ArrowNext, BankNote01, Check, Copy01, Lock01, Send01, ShoppingBag03, Tag01 } from "@untitledui/icons";
import { PageSection, cx, notify, toPersianDigits } from "@mfa-aff/ui";
import onboardingArt from "@mfa-aff/ui/assets/illustrations/onboarding.webp";
import { Button } from "@mfa-aff/ui/components/base/buttons/button";
import { ProgressBar } from "@mfa-aff/ui/components/base/progress-indicators/progress-indicators";
import { useClipboard } from "@mfa-aff/ui/hooks/use-clipboard";
import { useMe, useRecordShare, useUpdateOnboarding } from "../../api/me";
import { ShareMenu } from "../../components/ShareMenu";

type Step = {
    key: string;
    title: string;
    why: string;
    done: boolean;
    /** Not reachable yet (the Sheba step before the first approved commission). */
    locked?: boolean;
    icon: FC<{ className?: string }>;
};

const shareUrlOf = (refCode: string) => `https://aff.mizbanfa.net/r/${refCode}`;

/**
 * Getting-started checklist (spec §14.6.4, review 2026-10-07): code or link → first share →
 * discount code → first sale → Sheba. The Sheba step opens only after the first approved commission,
 * because it is needed to withdraw to a bank and nothing earlier (a withdrawal to the Mizbanfa
 * wallet needs no Sheba). Steps are computed by the server (`/me.onboarding`); copying and the
 * «ارسال» menu record steps 1 and 2. Hidden once all five are done or the partner chooses «بعداً».
 */
export const OnboardingChecklist = () => {
    const { data: me } = useMe();
    const update = useUpdateOnboarding();
    const clipboard = useClipboard();
    const record = useRecordShare();

    if (!me) return null;
    const o = me.onboarding;
    const url = shareUrlOf(me.affiliate.refCode);
    const withdrawDone = o.shebaDone || o.payoutDone;
    const total = 5;
    const doneCount = [o.refLinkDone, o.firstShareDone, o.firstCodeDone, o.firstSaleDone, o.firstApprovedDone && withdrawDone].filter(Boolean).length;
    if (o.dismissed || doneCount === total) return null;

    const copyLink = async () => {
        const r = await clipboard.copy(url);
        if (!r.success) {
            notify.error("کپی نشد", { description: "لینک را از صفحهٔ «بازاریابی با لینک» بردارید." });
            return;
        }
        record("copy");
        notify.success("لینک معرفی کپی شد", { description: "حالا آن را برای یک دوست، در یک گروه یا کانال بفرستید." });
    };

    const steps: Step[] = [
        {
            key: "link",
            title: "کد معرف یا لینک خود را بردارید",
            why: `کد معرف شما ${me.affiliate.refCode} است و لینک معرفی هم آماده است. هر خریدی که با یکی از این دو انجام شود به نام شما ثبت می‌شود.`,
            done: o.refLinkDone,
            icon: Copy01,
        },
        {
            key: "share",
            title: "اولین اشتراک‌گذاری",
            why: "لینک را برای یک دوست، در یک گروه یا کانالی که عضوش هستید بفرستید. همین که کسی با آن بیاید هم این قدم کامل می‌شود.",
            done: o.firstShareDone,
            icon: Send01,
        },
        {
            key: "code",
            title: "یک کد تخفیف بسازید",
            why: "اگر می‌خواهید مشتری تخفیف هم بگیرد، کد تخفیف بسازید. تخفیف را میزبان‌فا می‌دهد و مقدارش را خودتان تا سقف سطحتان انتخاب می‌کنید.",
            done: o.firstCodeDone,
            icon: Tag01,
        },
        {
            key: "sale",
            title: "اولین فروش",
            why: "وقتی کسی با کد یا لینک شما خرید کند، اینجا تیک می‌خورد و سفارش را در صفحهٔ «سفارش‌ها» می‌بینید. متن‌های آماده و بنرها در «کیت بازاریابی» است.",
            done: o.firstSaleDone,
            icon: ShoppingBag03,
        },
        {
            key: "withdraw",
            title: "انتخاب راه برداشت",
            why: o.firstApprovedDone
                ? "پورسانت شما قابل برداشت است. برداشت به کیف پول میزبان‌فا همین حالا و بدون شبا انجام می‌شود؛ برای واریز به حساب بانکی، شبای هم‌نام خودتان را ثبت کنید."
                : "بعد از اولین پورسانت تأییدشده لازم می‌شود. برداشت به کیف پول میزبان‌فا شبا نمی‌خواهد؛ شبا فقط برای واریز به حساب بانکی است.",
            done: o.firstApprovedDone && withdrawDone,
            locked: !o.firstApprovedDone,
            icon: BankNote01,
        },
    ];
    const nextKey = steps.find((s) => !s.done && !s.locked)?.key;

    const actionOf = (s: Step, isNext: boolean): ReactNode => {
        const color = isNext ? "primary" : "secondary";
        switch (s.key) {
            case "link":
                return (
                    <Button onPress={copyLink} size="sm" color={color} iconLeading={Copy01} className="max-sm:w-full">
                        کپی لینک معرفی
                    </Button>
                );
            case "share":
                return <ShareMenu url={url} text="برای خرید هاست و دامنه از میزبان‌فا از این لینک وارد شوید:" label="ارسال لینک" color={color} className="max-sm:w-full" />;
            case "code":
                return (
                    <Button href="/codes" size="sm" color={color} iconTrailing={ArrowNext} className="max-sm:w-full">
                        ساخت کد تخفیف
                    </Button>
                );
            case "sale":
                return (
                    <Button href="/kit" size="sm" color={color} iconTrailing={ArrowNext} className="max-sm:w-full">
                        ایده برای معرفی
                    </Button>
                );
            default:
                return (
                    <Button href="/wallet" size="sm" color={color} iconTrailing={ArrowNext} className="max-sm:w-full">
                        برداشت یا ثبت شبا
                    </Button>
                );
        }
    };

    return (
        <PageSection>
            <section
                className="relative flex flex-col gap-6 overflow-hidden rounded-2xl bg-primary bg-brand-soft p-5 shadow-xs ring-1 ring-secondary ring-inset md:p-6"
                aria-labelledby="onboarding-title"
            >
                <div className="flex items-start gap-4">
                    <img src={onboardingArt} alt="" aria-hidden="true" draggable={false} className="h-20 w-auto shrink-0 select-none max-sm:hidden md:h-24" />
                    <div className="flex min-w-0 flex-1 flex-col gap-1">
                        <p className="text-xs font-semibold text-brand-secondary dark:text-brand-300">راهنمای شروع</p>
                        <h2 id="onboarding-title" className="text-lg font-semibold text-primary">
                            پنج قدم تا اولین پورسانت
                        </h2>
                        <p className="text-sm text-tertiary">
                            مشتری با لینک یا کد شما خرید می‌کند، ۱۴ روز ضمانت می‌گذرد و بعد مبلغ قابل برداشت می‌شود.{" "}
                            <Link to="/how-it-works" className="rounded-xs font-medium text-brand-secondary underline-offset-4 outline-focus-ring hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 dark:text-brand-300">
                                بیشتر بخوانید
                            </Link>
                        </p>
                    </div>
                    <Button
                        color="tertiary"
                        size="sm"
                        isLoading={update.isPending}
                        onPress={() =>
                            update.mutate(
                                { dismissed: true },
                                {
                                    onSuccess: () =>
                                        notify.info("راهنما پنهان شد", { description: "هر وقت خواستید، صفحهٔ «چطور کار می‌کند» همین مسیر را توضیح می‌دهد." }),
                                },
                            )
                        }
                        aria-label="پنهان کردن راهنمای شروع"
                        className="shrink-0 max-sm:absolute max-sm:top-3 max-sm:end-3"
                    >
                        بعداً
                    </Button>
                </div>

                <div className="flex flex-col gap-2">
                    <div className="flex items-center justify-between text-sm">
                        <span className="font-medium text-secondary">پیشرفت شما</span>
                        <span className="text-tertiary digits-tabular">
                            {toPersianDigits(doneCount)} از ۵ قدم
                        </span>
                    </div>
                    <ProgressBar value={doneCount} max={total} progressClassName="bg-accent-500" />
                </div>

                <ol className="flex flex-col gap-2.5">
                    {steps.map((s, i) => {
                        const Icon = s.icon;
                        const isNext = s.key === nextKey;
                        return (
                            <li
                                key={s.key}
                                className={cx(
                                    "flex flex-col gap-3 rounded-xl p-4 ring-1 ring-inset sm:flex-row sm:items-center",
                                    s.done
                                        ? "bg-success-primary ring-utility-green-200 dark:bg-accent-500/10 dark:ring-accent-500/25"
                                        : isNext
                                          ? "bg-brand-primary_alt ring-brand-200 dark:bg-brand-500/10 dark:ring-brand-500/30"
                                          : "bg-secondary ring-secondary",
                                )}
                            >
                                <div className="flex min-w-0 flex-1 items-start gap-3">
                                    <span
                                        className={cx(
                                            "flex size-8 shrink-0 items-center justify-center rounded-full text-sm font-semibold",
                                            s.done
                                                ? "bg-success-solid text-white"
                                                : isNext
                                                  ? "bg-brand-solid text-white"
                                                  : "bg-primary text-tertiary ring-1 ring-secondary ring-inset",
                                        )}
                                        aria-hidden="true"
                                    >
                                        {s.done ? <Check className="size-4" /> : s.locked ? <Lock01 className="size-4" /> : toPersianDigits(i + 1)}
                                    </span>
                                    <div className="flex min-w-0 flex-1 flex-col gap-1">
                                        <h3 className="flex items-center gap-2 text-sm font-semibold text-primary">
                                            <Icon className={cx("size-4 shrink-0", s.done ? "text-fg-success-secondary" : isNext ? "text-fg-brand-secondary" : "text-fg-quaternary")} aria-hidden="true" />
                                            {s.title}
                                            <span className="sr-only">{s.done ? "، انجام شد" : s.locked ? "، هنوز لازم نیست" : ""}</span>
                                        </h3>
                                        {(!s.done || isNext) && <p className="text-sm text-tertiary">{s.why}</p>}
                                    </div>
                                </div>
                                <div className="shrink-0 ps-11 sm:ps-0">
                                    {s.done ? (
                                        <span className="inline-flex items-center gap-1.5 text-sm font-medium text-success-primary">
                                            <Check className="size-4" aria-hidden="true" />
                                            انجام شد
                                        </span>
                                    ) : s.locked ? (
                                        <span className="text-xs font-medium text-quaternary">بعد از اولین پورسانت</span>
                                    ) : (
                                        actionOf(s, isNext)
                                    )}
                                </div>
                            </li>
                        );
                    })}
                </ol>
            </section>
        </PageSection>
    );
};
